import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Outlet } from 'react-router-dom';
import { SideNav } from './SideNav';
import { PresetNameProvider } from '../utils/PresetNameContext';
import '../styles/ascend-tokens.css';

const STORAGE_KEY = 'qs-sidenav-collapsed';

export interface AppShellProps {
  projectName?: string;
  clientName?: string;
}

const GradientStripe = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--green);
  z-index: 200;
  pointer-events: none;
`;

const Shell = styled.div`
  display: flex;
  min-height: 100vh;
`;

const ContentArea = styled.main<{ $collapsed: boolean }>`
  margin-left: ${({ $collapsed }) => ($collapsed ? '56px' : '240px')};
  width: ${({ $collapsed }) => ($collapsed ? 'calc(100vw - 56px)' : 'calc(100vw - 240px)')};
  min-height: 100vh;
  background: var(--surface);
  transition: margin-left 200ms ease, width 200ms ease;
  padding-top: 4px;
`;

export function AppShell({ projectName = '', clientName = '' }: Readonly<AppShellProps>) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  function handleToggle() {
    setCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // storage unavailable — state still applied
      }
      return next;
    });
  }

  return (
    <PresetNameProvider>
      <Shell>
        <GradientStripe />
        <SideNav
          projectName={projectName}
          clientName={clientName}
          collapsed={collapsed}
          onToggle={handleToggle}
        />
        <ContentArea $collapsed={collapsed}>
          <Outlet />
        </ContentArea>
      </Shell>
    </PresetNameProvider>
  );
}
